import {
  type VirtualViewportPixelAccessor,
  type Viewport,
  type HasBackgroundColor,
  type HasAverageColor,
  isVirtualViewportPixelAccessor,
  isHasAverageColor,
  isHasBackgroundColor,
} from "../pixel-accessor";
import type { Color } from "../types";

/**
 * Image adapter interface.
 */
export interface ImageAdapter<ResourceType>
  extends VirtualViewportPixelAccessor,
    HasBackgroundColor,
    HasAverageColor {
  /**
   * Sets image virtual viewport.
   *
   * @param viewport
   */
  setViewport(viewport: Viewport): void;

  /**
   * Sets image background color.
   *
   * @param color
   */
  setBackgroundColor(color: Color): void;

  /**
   * Returns new blank image with given virtual viewport.
   *
   * @param viewport
   */
  getBlank(viewport: Viewport): ImageAdapter<ResourceType>;

  /**
   * Scales image and its virtual viewport.
   *
   * @param scale
   */
  scale(scale: number): ImageAdapter<ResourceType>;

  /**
   * Returns underlying image resource.
   */
  getResource(): ResourceType;

  /**
   * Method called after distortion process to synchronize distorted image data with underlying image resource if needed.
   */
  commit(): void;
}

/**
 * Checks if passed object implements ImageAdapter interface.
 *
 * @param obj
 */
export function isImageAdapter(obj: unknown): obj is ImageAdapter<unknown> {
  type C = Partial<ImageAdapter<unknown>>;
  return (
    isVirtualViewportPixelAccessor(obj) &&
    isHasAverageColor(obj) &&
    isHasBackgroundColor(obj) &&
    typeof (obj as C).setViewport === "function" &&
    typeof (obj as C).setBackgroundColor === "function" &&
    typeof (obj as C).getBlank === "function" &&
    typeof (obj as C).scale === "function" &&
    typeof (obj as C).getResource === "function" &&
    typeof (obj as C).commit === "function"
  );
}
